import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Coffee, Pause, Play, Square } from 'lucide-react';
import { Button, Card, cn } from '@ai-tutor/ui';
import { WELLBEING_PATHS } from '../wellbeingPaths';
import { ParkingLot } from './ParkingLot';
import type { FocusViewModel } from './focusViewModel';

export interface FocusPanelProps {
  view: FocusViewModel;
  onPause: () => void;
  onResume: () => void;
  onFinish: () => void;
  onOpenBreak: () => void;
  onNextBlock: () => void;
  onAddNote: (text: string) => void;
  onRemoveNote: (id: string) => void;
  /** Called when the timer link is followed, so the panel can close. */
  onNavigate?: () => void;
  className?: string;
}

function PanelControls(props: FocusPanelProps) {
  const { view } = props;
  if (view.phase === 'break') {
    return (
      <div className='flex gap-2'>
        <Button
          size='sm'
          onClick={props.onOpenBreak}
          className='flex-1 max-sm:h-11'
        >
          <Coffee className='mr-1.5 h-4 w-4' aria-hidden='true' />
          Take your break
        </Button>
        <Button
          size='sm'
          className='max-sm:h-11'
          variant='outline'
          onClick={props.onNextBlock}
        >
          Back to focus
        </Button>
      </div>
    );
  }
  const paused = view.phase === 'paused';
  const flow = view.progress === null;
  return (
    <div className='flex gap-2'>
      {paused ? (
        <Button
          size='sm'
          onClick={props.onResume}
          className='flex-1 max-sm:h-11'
        >
          <Play className='mr-1.5 h-4 w-4' aria-hidden='true' />
          Resume
        </Button>
      ) : (
        <Button
          size='sm'
          variant='outline'
          onClick={props.onPause}
          className='flex-1 max-sm:h-11'
        >
          <Pause className='mr-1.5 h-4 w-4' aria-hidden='true' />
          Pause
        </Button>
      )}
      <Button
        size='sm'
        className='max-sm:h-11'
        variant='ghost'
        onClick={props.onFinish}
      >
        <Square className='mr-1.5 h-3.5 w-3.5' aria-hidden='true' />
        {flow ? "I'm done" : 'End block'}
      </Button>
    </div>
  );
}

/**
 * What the Navbar pill opens: the timer, its controls and the parking lot,
 * without leaving the lesson you are in.
 */
export const FocusPanel = forwardRef<HTMLDivElement, FocusPanelProps>(
  (props, ref) => {
    const { view } = props;
    const onBreak = view.phase === 'break';
    const heading = onBreak
      ? view.breakKind === 'long'
        ? 'Long break'
        : 'Break'
      : view.phase === 'paused'
        ? 'Paused'
        : 'Focus';
    return (
      <Card
        ref={ref}
        elevation='md'
        role='dialog'
        aria-label='Focus timer'
        className={cn(
          'w-80 space-y-4 bg-popover p-4 dark:bg-popover',
          props.className
        )}
      >
        <div className='flex items-center justify-between'>
          <span
            className={cn(
              'text-xs font-semibold uppercase tracking-wide',
              onBreak ? 'text-tint-brand-foreground' : 'text-muted-foreground'
            )}
          >
            {heading}
          </span>
          {view.round && (
            <span className='font-mono text-xs text-muted-foreground'>
              Round {view.round.current} of {view.round.total}
            </span>
          )}
        </div>
        <div>
          <p className='font-mono text-4xl font-semibold tabular-nums text-foreground'>
            {view.timeLabel}
          </p>
          {view.intention && !onBreak && (
            <p className='mt-1 truncate text-sm text-muted-foreground'>
              {view.intention}
            </p>
          )}
        </div>
        <PanelControls {...props} />
        {!onBreak && (
          <ParkingLot
            notes={view.parkingLot}
            onAdd={props.onAddNote}
            onRemove={props.onRemoveNote}
          />
        )}
        <Link
          to={WELLBEING_PATHS.focus}
          onClick={props.onNavigate}
          className='tap-target flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground'
        >
          Open the timer
          <ArrowRight className='h-3.5 w-3.5' aria-hidden='true' />
        </Link>
      </Card>
    );
  }
);
FocusPanel.displayName = 'FocusPanel';
